'use client'

import { useLocale, useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { ISubscriptionPrice } from '@/api/stripe/get-stripe-prices-query'
import { Locale, toLocalePriceString } from '@/setup/locales'
import { compact } from '@/utils/functions/compact'
import { PricingCard } from './pricing-card'
import { PricingDownload } from './pricing-download'
import { PricingHeader } from './pricing-header'

type IPricingLandingPageProps = {
    stripePrices: ISubscriptionPrice[] | null
}

export const PricingLandingPage = ({ stripePrices }: IPricingLandingPageProps) => {
    const t = useTranslations()
    const locale = useLocale()

    const monthlyPrice = stripePrices?.find(
        (stripePrice) => stripePrice.subscriptionType === 'monthly'
    )

    const yearlyPrice = stripePrices?.find(
        (stripePrice) => stripePrice.subscriptionType === 'yearly'
    )

    const twoYearsPrice = stripePrices?.find(
        (stripePrice) => stripePrice.subscriptionType === 'twoYears'
    )

    const pricingCards = useMemo(
        () =>
            compact([
                monthlyPrice && {
                    title: t('pricing.items.monthly.title'),
                    price: t('pricing.items.monthly.price', {
                        price: toLocalePriceString({
                            price: 6.99,
                            locale: locale as Locale,
                        }),
                    }),
                    billingCycle: t('pricing.items.monthly.billingCycle', {
                        monthlyPrice: toLocalePriceString({
                            price: 6.99,
                            locale: locale as Locale,
                        }),
                    }),
                    benefits: [
                        t('pricing.items.monthly.benefits.0'),
                        t('pricing.items.monthly.benefits.1'),
                        t('pricing.items.monthly.benefits.2'),
                    ],
                },
                yearlyPrice && {
                    title: t('pricing.items.annual.title'),
                    price: t('pricing.items.annual.price', {
                        price: toLocalePriceString({
                            price: 5.99,
                            locale: locale as Locale,
                        }),
                    }),
                    billingCycle: t('pricing.items.annual.billingCycle', {
                        yearlyPrice: toLocalePriceString({
                            price: 71.88,
                            locale: locale as Locale,
                        }),
                    }),
                    benefits: [
                        t('pricing.items.annual.benefits.0'),
                        t('pricing.items.annual.benefits.1'),
                        t('pricing.items.annual.benefits.2'),
                    ],
                },
                twoYearsPrice && {
                    title: t('pricing.items.biannual.title'),
                    price: t.rich('pricing.items.biannual.price', {
                        price: toLocalePriceString({
                            price: 3.99,
                            locale: locale as Locale,
                        }),
                    }),
                    billingCycle: t('pricing.items.biannual.billingCycle', {
                        twoYearsPrice: toLocalePriceString({
                            price: 95.76,
                            locale: locale as Locale,
                        }),
                    }),
                    benefits: [
                        t('pricing.items.biannual.benefits.0'),
                        t('pricing.items.biannual.benefits.1'),
                        t('pricing.items.biannual.benefits.2'),
                    ],
                },
            ]),
        [t, locale, yearlyPrice, twoYearsPrice, monthlyPrice]
    )

    return (
        <div className="flex w-full flex-col items-center mb-20">
            <div className="px-4">
                <PricingHeader />
            </div>

            <div className="flex flex-col md:flex-row max-w-[1040px] gap-8 px-4 pb-14">
                {pricingCards.map((pricingCard) => (
                    <PricingCard
                        key={`price-card` + pricingCard.title}
                        title={pricingCard.title}
                        price={pricingCard.price}
                        billingCycle={pricingCard.billingCycle}
                        benefits={pricingCard.benefits}
                    />
                ))}
            </div>

            <div className="w-full flex justify-center px-4">
                <PricingDownload />
            </div>
        </div>
    )
}
